<app-modal
  [heading]="flag ? 'Flag this' : 'Report a suggestion or bug'"
  submitLabel="Send report"
  [formGroup]="form"
  [pending]="pending()"
  [error]="error()"
  [dismissWith]="dismissReport"
  (submitted)="submit()"
>
  <div class="report-dialog__form">
    @if (flag; as f) {
      <!-- FLOW-347 (DOC-79 §5A): carried, filled in, not editable. -->
      <dl class="report-dialog__carried" data-testid="flag-carried">
        <dt>Application</dt>
        <dd>{{ f.application }}</dd>
        <dt>Assessment run</dt>
        <dd>{{ f.run ?? 'No run recorded' }}{{ f.as_at ? ' · ' + f.as_at : '' }}</dd>
        <dt>Rulebook version</dt>
        <dd>{{ f.rulebook_version || 'Not recorded on this run' }}</dd>
        <dt>Prompt version</dt>
        <dd>
          {{ f.prompt_version || 'Not recorded on this run'
          }}{{ f.prompt_label ? ' (' + f.prompt_label + ')' : '' }}
        </dd>
        @if (f.term) {
          <dt>Term</dt>
          <dd>{{ f.term }} — {{ f.term_rule }}</dd>
        }
        <dt>{{ f.term ? 'In this case' : 'The line' }}</dt>
        <dd>{{ f.line }}</dd>
        <dt>Why, as shown</dt>
        <dd>
          @if (f.why) {
            <pre class="report-dialog__why">{{ f.why | json }}</pre>
          } @else {
            No basis recorded for this
          }
        </dd>
      </dl>

      <mat-radio-group
        formControlName="problem"
        class="report-dialog__problems"
        aria-label="What kind of problem is it?"
      >
        <span class="report-dialog__label">What kind of problem is it?</span>
        @for (p of problems; track p.value) {
          <mat-radio-button [value]="p.value">{{ p.label }}</mat-radio-button>
        }
      </mat-radio-group>
      @if (form.controls.problem.touched && form.controls.problem.invalid) {
        <mat-error>Choose what kind of problem this is.</mat-error>
      }
    } @else {
      <mat-button-toggle-group
        formControlName="kind"
        class="report-dialog__kind"
        aria-label="What are you reporting?"
      >
        <mat-button-toggle value="BUG">
          <app-icon name="bug" [size]="16" />
          Something's broken
        </mat-button-toggle>
        <mat-button-toggle value="SUGGESTION">
          <app-icon name="lightbulb" [size]="16" />
          I have a suggestion
        </mat-button-toggle>
      </mat-button-toggle-group>
    }

    @if (flag) {
      <mat-form-field appearance="outline" class="report-dialog__message">
        <mat-label>One line — what is wrong?</mat-label>
        <input matInput formControlName="message" />
        @if (form.controls.message.touched && form.controls.message.hasError('required')) {
          <mat-error>Please describe what you want to report.</mat-error>
        }
      </mat-form-field>
    } @else {
      <mat-form-field appearance="outline" class="report-dialog__message">
        <mat-label>What happened?</mat-label>
        <textarea
          matInput
          formControlName="message"
          rows="5"
          cdkFocusInitial
          placeholder="Tell us what you expected, and what happened instead."
        ></textarea>
        @if (form.controls.message.touched && form.controls.message.hasError('required')) {
          <mat-error>Please describe what you want to report.</mat-error>
        }
      </mat-form-field>
    }

    <!-- Screenshot: optional on every device, offered two ways. -->
    <div class="report-dialog__screenshot">
      <span class="report-dialog__label"
        >Screenshot <span class="report-dialog__optional">(optional)</span></span
      >

      @if (previewUrl(); as preview) {
        <div class="report-dialog__preview">
          <img [src]="preview" alt="The screenshot attached to this report" />
          <div class="report-dialog__preview-meta">
            <app-icon name="image" [size]="16" />
            <span>Screenshot attached · {{ screenshotSize() }}</span>
          </div>
          <button
            mat-icon-button
            type="button"
            class="report-dialog__preview-remove"
            aria-label="Remove screenshot"
            matTooltip="Remove screenshot"
            (click)="removeScreenshot()"
          >
            <app-icon name="x" [size]="16" />
          </button>
        </div>
      } @else {
        <div class="report-dialog__screenshot-actions">
          @if (canCapture) {
            <button
              mat-stroked-button
              type="button"
              [disabled]="capturing()"
              (click)="captureScreenshot()"
            >
              @if (capturing()) {
                <mat-spinner diameter="18" />
                Capturing…
              } @else {
                <app-icon name="camera" [size]="16" />
                Take a screenshot
              }
            </button>
          }
          <button mat-stroked-button type="button" (click)="fileInput.click()">
            <app-icon name="upload" [size]="16" />
            {{ canCapture ? 'Attach one instead' : 'Attach a screenshot' }}
          </button>
          <input #fileInput type="file" accept="image/*" hidden (change)="onFileSelected($event)" />
        </div>
        @if (!canCapture) {
          <p class="report-dialog__hint">Take a screenshot on your device, then attach it here.</p>
        }
      }
    </div>

    <!-- Shown in full so nobody is surprised by what the report carries. -->
    <details class="report-dialog__context">
      <summary>What we'll send with this</summary>
      <dl>
        <dt>Page</dt>
        <dd>{{ context.url }}</dd>
        <dt>Browser window</dt>
        <dd>{{ context.viewport_width }} × {{ context.viewport_height }}</dd>
        <dt>Screen</dt>
        <dd>
          {{ context.screen_width }} × {{ context.screen_height }} (at
          {{ context.device_pixel_ratio }}×)
        </dd>
        <dt>Device</dt>
        <dd>{{ context.platform || '—' }}</dd>
        <dt>Browser</dt>
        <dd class="report-dialog__ua">{{ context.user_agent }}</dd>
      </dl>
      <p class="report-dialog__hint">
        Your name and email address are included so we can reply to you.
      </p>
    </details>
  </div>
</app-modal>
